import { Component, OnInit, ViewChild } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { StoreService } from '@se-po/se-household-setup-data-access-store'
import { DobParser } from '@se-po/shared-data-access-parsers'
import { AnalyticsService, AuthSessionService, InviteService, UserServiceCsrfTokenService } from '@se-po/shared-data-access-services'
import { SetupBarService } from '@se-po/shared-ui-setup-bar'
import { CookieService } from 'ngx-cookie-service'
import { SeFeDomainEnvironmentService } from 'se-fe-domain-environment'
import { UserServiceResources as us } from 'se-resource-types'

import { ContactInfoComponent } from '@se-po/shared-ui-contact-info'
import { PersonaInfoComponent } from '@se-po/shared-ui-persona-info'
import { forkJoin } from 'rxjs'
import { SelfInfoForm } from './self-info-form.service'
import { SelfInfoSubmitter } from './self-info-submit.service'

@Component({
  selector: 'se-po-household-setup-feature-self-info',
  templateUrl: './self-info.component.html',
  styleUrls: ['./self-info.component.scss'],
})
export class SelfInfoComponent implements OnInit {

  @ViewChild('contactInfo') public contactInfo: ContactInfoComponent
  @ViewChild('emailMatchForm') public emailMatchForm: HTMLFormElement
  @ViewChild('personaInfo') public personaInfo: PersonaInfoComponent

  public actionUrl: string
  public ready = false
  public csrfToken: string

  private readonly app = 'SingleSignOn'
  private readonly page = 'SetupHousehold'
  private readonly label = 'Step1'
  private email: string

  constructor(
    private analyticsService: AnalyticsService,
    private authSession: AuthSessionService,
    private cookieService: CookieService,
    private env: SeFeDomainEnvironmentService,
    public formService: SelfInfoForm,
    private inviteService: InviteService,
    private route: ActivatedRoute,
    public setupBarService: SetupBarService,
    public submitter: SelfInfoSubmitter,
    public store: StoreService,
    private csrfTokenService: UserServiceCsrfTokenService
  ) {}

  public get form(): UntypedFormGroup {
    return this.formService.form
  }

  public ngOnInit(): void {
    if (this.cookieService.get(this.submitter.underAgeCookieName)) {
      this.submitter.agelock = true
    }
    this.formService.createForm()
    this.actionUrl = `${this.env.getUrl('userService')}users/sign_in`
    this.analyticsService.pageView(this.app, this.page, this.label)
    this.setupBarService.setAttributes({
      title: 'Create Account',
      percentComplete: 25
    })

    forkJoin({
      auth_session: this.authSession.find(),
      csrf_token: this.csrfTokenService.fetchCsrfToken()
    }).subscribe( ({ auth_session, csrf_token }) => {
      this.csrfToken = csrf_token.csrf_token
      this.initAuthSession(auth_session)
      if (auth_session.invite_token) return this.lookupInvite(auth_session.invite_token)
      this.setReady()
    })
  }

  public onContactInfoFormInit(contactInfo: ContactInfoComponent): void {
    if (this.email) contactInfo.form.patchValue({ email: this.email })
    if (this.store.disableEmail) contactInfo.form.get('email').disable()
  }

  public initAuthSession(auth_session: us.AuthSession): void {
    this.store.authSession = auth_session
    const email = this.store.authSessionEmailAddress
    if (email) this.email = email
    else {
      this.route.queryParams.subscribe(p => {
        if (p.email) this.email = p.email
      }
    )}
  }

  public emailMatchSignIn(): void {
    this.submitter.submitting = true
    this.submitter.emailMatchSignIn(this.emailMatchForm)
  }

  private lookupInvite(token: string): void {
    this.inviteService.find(token)
      .subscribe({
        next: (result) => {
          this.store.invite = result
          if (!result?.is_guardian && result?.persona) {
            this.form.patchValue({
              firstName: result.persona.first_name || '',
              lastName: result.persona.last_name || '',
              dateOfBirth: DobParser.isoToMm(result.persona.date_of_birth) || '',
            })
          }
          this.setReady()
        },
        error: () => {
          // error finding invite, ignore and move on
          this.setReady()
        }
      })
  }

  private setReady(): void {
    this.ready = true
  }
}
